import { generateClient, get, isCancelError, post } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";

const client = generateClient();

export async function requestListWebinar(dispatch, payload) {
  try {
    const {
      limit = 10,
      page,
      startOfDay,
      endOfDay,
      nextToken = null,
      type = "FREE",
      includeAllTypes = false,
    } = payload;

    dispatch({
      type: "REQUEST_LIST_WEBINAR",
      payload: { page },
    });

    const types = includeAllTypes ? ["FREE", "PAID"] : [type];

    const results = await Promise.all(
      types.map(async (typeItem) => {
        const params = {
          type: typeItem,
          sortDirection: "DESC",
          limit,
          nextToken,
          ...(startOfDay &&
            endOfDay && { scheduledAt: { between: [startOfDay, endOfDay] } }),
        };

        const res = await client.graphql({
          query: webinarsByTypeScheduledAt,
          variables: params,
        });

        return {
          items: res.data.webinarsByTypeScheduledAt.items,
          nextToken: res.data.webinarsByTypeScheduledAt.nextToken,
        };
      }),
    );

    let items = results.flatMap((r) => r.items);

    if (includeAllTypes) {
      items.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    }

    let fetchedUrl = {};

    items = await Promise.all(
      items.map(async (item) => {
        let updatedItem = item;
        if (item.image?.key) {
          const { identityId, key } = item.image;
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: `protected/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                imageUrl: signedUrl,
              };
            }
          } catch (error) {}
        }

        return updatedItem;
      }),
    );

    const nextPage = page + 1;

    dispatch({
      type: "LIST_WEBINAR_SUCCESS",
      payload: {
        items,
        nextToken: includeAllTypes ? null : results[0]?.nextToken || null,
        nextPage,
      },
    });
  } catch (error) {
    dispatch({ type: "LIST_WEBINAR_ERROR", error });
  }
}

export async function requestGetPublicWebinarDetail(dispatch, payload) {
  try {
    const { id } = payload;
    dispatch({ type: "REQUEST_GET_WEBINAR_DETAIL" });

    const request = get({
      apiName: "webinar",
      path: `/webinar/${id}/detail`,
    });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.data) {
      throw new Error("Webinar not found");
    }

    const result = res.data;

    dispatch({
      type: "GET_WEBINAR_DETAIL_SUCCESS",
      payload: { item: result },
    });
  } catch (error) {
    dispatch({ type: "GET_WEBINAR_DETAIL_ERROR", error });
  }
}

export async function requestGetWebinarDetail(dispatch, payload) {
  try {
    const { id } = payload;
    dispatch({ type: "REQUEST_GET_WEBINAR_DETAIL" });

    const params = {
      id,
    };

    const res = await client.graphql({
      query: getWebinar,
      variables: params,
    });
    let result = res.data.getWebinar;

    let fetchedUrl = {};
    let updatedItem = result;

    if (result?.image?.key) {
      const { key, identityId } = result.image;
      try {
        let signedUrl = "";
        if (fetchedUrl[key]) {
          signedUrl = fetchedUrl[key];
        } else {
          const urlResult = await getUrl({
            path: `protected/${identityId}/${key}`,
            options: {
              useAccelerateEndpoint: true,
            },
          });
          signedUrl = urlResult.url.href;
          fetchedUrl[key] = signedUrl;
        }
        if (signedUrl) {
          updatedItem = {
            ...updatedItem,
            imageUrl: signedUrl,
          };
        }
      } catch (error) {}
    }

    let speakers = result?.speakers || [];
    speakers = await Promise.all(
      speakers.map(async (speaker) => {
        try {
          const consultantID = speaker.consultantID || null;
          if (!consultantID) {
            return speaker;
          }
          const consultantRes = await client.graphql({
            query: getConsultant,
            variables: {
              id: consultantID,
            },
          });

          const consultant = consultantRes.data.getConsultant;

          if (!consultant) {
            return speaker;
          }

          if (!consultant.active) {
            return speaker;
          }

          const { user } = consultant;
          if (user?.image?.key) {
            const { identityId, key: imageKey } = user.image;
            try {
              const urlResult = await getUrl({
                path: `protected/${identityId}/${imageKey}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });

              const signedUrl = urlResult.url.href;

              if (signedUrl) {
                user.imageUrl = signedUrl;
              }
            } catch (error) {
              // Error fetching user image
            }
          }

          return {
            ...speaker,
            name: consultant.user?.name || speaker.name,
            consultant: {
              ...consultant,
              user,
            },
          };
        } catch (error) {
          // Error processing speaker, return original speaker
          return speaker;
        }
      }),
    );

    updatedItem = {
      ...updatedItem,
      speakers,
    };

    dispatch({
      type: "GET_WEBINAR_DETAIL_SUCCESS",
      payload: { item: updatedItem },
    });
  } catch (error) {
    dispatch({ type: "GET_WEBINAR_DETAIL_ERROR", error });
  }
}

export async function requestWebinarRegistration(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_WEBINAR_REGISTRATION",
      payload: { request: null },
    });

    const { webinarID, name, organization, industry, jobTitle, note } = payload;

    const request = post({
      apiName: "webinar",
      path: "/webinar/register",
      options: {
        body: {
          webinar_id: webinarID,
          ...(name && { name }),
          ...(organization && { organization }),
          ...(industry && { industry }),
          ...(jobTitle && { jobTitle }),
          ...(note && { note }),
        },
      },
    });

    dispatch({ type: "REQUEST_WEBINAR_REGISTRATION", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    dispatch({
      type: "WEBINAR_REGISTRATION_SUCCESS",
      payload: { registration: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "WEBINAR_REGISTRATION_ERROR", error: null });
    } else {
      dispatch({ type: "WEBINAR_REGISTRATION_ERROR", error });
    }
  }
}

export async function requestGetWebinarRegistrationStatus(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_WEBINAR_REGISTRATION_STATUS" });

    const { id } = payload;

    const request = get({
      apiName: "webinar",
      path: `/webinar/${id}/registration`,
    });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.data) {
      throw new Error("No registration data found");
    }

    dispatch({
      type: "GET_WEBINAR_REGISTRATION_STATUS_SUCCESS",
      payload: { status: res.data },
    });
  } catch (error) {
    dispatch({ type: "GET_WEBINAR_REGISTRATION_STATUS_ERROR", error });
  }
}

export async function requestWebinarCertificate(dispatch, payload) {
  try {
    const request = get({
      apiName: "webinar",
      path: `/webinar/${payload.id}/certificate`,
    });

    dispatch({ type: "REQUEST_GET_WEBINAR_CERTIFICATE", payload: { request } });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.data) {
      throw new Error("No certificate data found");
    }

    dispatch({
      type: "GET_WEBINAR_CERTIFICATE_SUCCESS",
      payload: { certificate: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "GET_WEBINAR_CERTIFICATE_ERROR", error: null });
    } else {
      dispatch({ type: "GET_WEBINAR_CERTIFICATE_ERROR", error });
    }
  }
}

export async function requestVerifyWebinarCertificate(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_VERIFY_WEBINAR_CERTIFICATE" });

    const { id } = payload;

    const request = get({
      apiName: "webinar",
      path: `/verify/certificate/${id}`,
    });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.data) {
      throw new Error("No certificate verification data found");
    }

    dispatch({
      type: "VERIFY_WEBINAR_CERTIFICATE_SUCCESS",
      payload: { verification: res.data },
    });
  } catch (error) {
    dispatch({ type: "VERIFY_WEBINAR_CERTIFICATE_ERROR", error });
  }
}

export async function requestGetConsultantWebinars(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_GET_CONSULTANT_WEBINARS" });

    const { id } = payload;

    const request = get({
      apiName: "webinar",
      path: `/consultant/${id}/webinars`,
    });

    const { body } = await request.response;
    const res = await body.json();

    if (!res.data.webinars) {
      throw new Error("No consultant webinars data found");
    }

    let items = res.data.webinars;
    let fetchedUrl = {};

    items = await Promise.all(
      items.map(async (item) => {
        let updatedItem = item;
        if (item.image?.key) {
          const { identityId, key } = item.image;
          try {
            let signedUrl = "";
            if (fetchedUrl[key]) {
              signedUrl = fetchedUrl[key];
            } else {
              const urlResult = await getUrl({
                path: `protected/${identityId}/${key}`,
                options: {
                  useAccelerateEndpoint: true,
                },
              });
              signedUrl = urlResult.url.href;
              fetchedUrl[key] = signedUrl;
            }
            if (signedUrl) {
              updatedItem = {
                ...updatedItem,
                imageUrl: signedUrl,
              };
            }
          } catch (error) {}
        }
        return updatedItem;
      }),
    );

    dispatch({
      type: "GET_CONSULTANT_WEBINARS_SUCCESS",
      payload: { items },
    });
  } catch (error) {
    dispatch({ type: "GET_CONSULTANT_WEBINARS_ERROR", error });
  }
}

export async function clearWebinarDetail(dispatch) {
  dispatch({ type: "CLEAR_WEBINAR_DETAIL" });
}

export async function clearWebinarCertificate(dispatch) {
  dispatch({ type: "CLEAR_WEBINAR_CERTIFICATE" });
}

export async function clearWebinarRegistration(dispatch) {
  dispatch({ type: "CLEAR_WEBINAR_REGISTRATION" });
}

export async function clearWebinarErrors(dispatch) {
  dispatch({ type: "CLEAR_WEBINAR_ERRORS" });
}

const webinarsByTypeScheduledAt = /* GraphQL */ `
  query WebinarsByTypeScheduledAt(
    $type: WebinarType
    $scheduledAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelWebinarFilterInput
    $limit: Int
    $nextToken: String
  ) {
    webinarsByTypeScheduledAt(
      type: $type
      scheduledAt: $scheduledAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        title
        body
        registrationUrl
        easyRegistration
        type
        earnCertificate
        streamUrl
        recordingUrl
        zoom {
          meetingID
          passcode
        }
        speakers {
          name
          role
          company
          job
          consultantID
        }
        scheduledAt
        duration
        price
        normalPrice
        memberPrice
        image {
          bucket
          region
          key
          identityId
        }
        documents {
          bucket
          region
          key
          identityId
        }
        minJoinDuration
        minPostTestScore
        maxParticipants
        participantCount
        participantCountUpdatedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getWebinar = /* GraphQL */ `
  query GetWebinar($id: ID!) {
    getWebinar(id: $id) {
      id
      title
      body
      registrationUrl
      easyRegistration
      type
      earnCertificate
      streamUrl
      recordingUrl
      zoom {
        meetingID
        passcode
      }
      speakers {
        name
        role
        company
        job
        consultantID
      }
      scheduledAt
      duration
      price
      normalPrice
      memberPrice
      image {
        bucket
        region
        key
        identityId
      }
      documents {
        bucket
        region
        key
        identityId
      }
      streamingBackground {
        bucket
        region
        key
        identityId
      }
      minJoinDuration
      minPostTestScore
      maxParticipants
      participantCount
      participantCountUpdatedAt
      createdAt
      updatedAt
    }
  }
`;

const getConsultant = /* GraphQL */ `
  query GetConsultant($id: ID!) {
    getConsultant(id: $id) {
      id
      acceptOnline
      active
      acceptCall
      acceptZoom
      acceptMeeting
      startTimes
      endTimes
      activeDays
      user {
        id
        name
        image {
          bucket
          region
          key
          identityId
        }
      }
      services {
        items {
          id
          consultantID
          serviceID
          service {
            id
            name
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      city
      experience
      workStartYear
      certificates {
        name
        level
        year
        issuedBy
      }
      consultationPrice
      callPrice
      audioPrice
      zoomPrice
      lastSeen
      totalRating
      rating
      totalMonthlyConsultation
      totalConsultation
      averageResponse
    }
  }
`;
